import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  TextInput,
  Textarea,
  Center,
  Image,
  SimpleGrid,
  FileButton,
  Box,
  Chip,
  Select,
  NumberInput,
  Stepper,
  Badge,
  Divider,
  Alert,
  ThemeIcon,
  PasswordInput,
  Paper,
} from '@mantine/core';
import {
  IconCamera,
  IconX,
  IconPlus,
  IconCheck,
  IconArrowLeft,
  IconArrowRight,
  IconUser,
  IconInfoCircle,
  IconHeart,
  IconSettings,
  IconPhoto,
  IconEye,
  IconLock,
  IconVideo,
  IconBuildingBank,
  IconShieldCheck,
} from '@tabler/icons-react';
import { Tabs } from '@mantine/core';

// ── Foot attribute tags ─────────────────────────────────────────────────────
const footAttributes = [
  { value: 'small-feet', label: 'Small Feet' },
  { value: 'large-feet', label: 'Large Feet' },
  { value: 'medium-feet', label: 'Medium Feet' },
  { value: 'petite-feet', label: 'Petite Feet' },
  { value: 'big-feet', label: 'Big Feet' },
  { value: 'fat-feet', label: 'Fat Feet' },
  { value: 'thin-feet', label: 'Thin Feet' },
  { value: 'wide-feet', label: 'Wide Feet' },
  { value: 'narrow-feet', label: 'Narrow Feet' },
  { value: 'curvy-feet', label: 'Curvy Feet' },
  { value: 'tan-feet', label: 'Tan Feet' },
  { value: 'fair-feet', label: 'Fair Feet' },
  { value: 'dark-feet', label: 'Dark Feet' },
  { value: 'olive-feet', label: 'Olive Feet' },
  { value: 'brown-feet', label: 'Brown Feet' },
  { value: 'pale-feet', label: 'Pale Feet' },
  { value: 'long-toes', label: 'Long Toes' },
  { value: 'short-toes', label: 'Short Toes' },
  { value: 'round-toes', label: 'Round Toes' },
  { value: 'square-toes', label: 'Square Toes' },
  { value: 'pointed-toes', label: 'Pointed Toes' },
  { value: 'oval-toes', label: 'Oval Toes' },
  { value: 'missing-toe', label: 'Missing Toe' },
  { value: 'extra-toe', label: 'Extra Toe' },
  { value: 'webbed-toes', label: 'Webbed Toes' },
  { value: 'curled-toes', label: 'Curled Toes' },
  { value: 'bunions', label: 'Bunions' },
  { value: 'red-nail-polish', label: 'Red Nail Polish' },
  { value: 'pink-nail-polish', label: 'Pink Nail Polish' },
  { value: 'purple-nail-polish', label: 'Purple Nail Polish' },
  { value: 'blue-nail-polish', label: 'Blue Nail Polish' },
  { value: 'black-nail-polish', label: 'Black Nail Polish' },
  { value: 'white-nail-polish', label: 'White Nail Polish' },
  { value: 'gold-nail-polish', label: 'Gold Nail Polish' },
  { value: 'silver-nail-polish', label: 'Silver Nail Polish' },
  { value: 'nude-nail-polish', label: 'Nude Nail Polish' },
  { value: 'neon-nail-polish', label: 'Neon Nail Polish' },
  { value: 'glitter-nail-polish', label: 'Glitter Nail Polish' },
  { value: 'matte-nail-polish', label: 'Matte Nail Polish' },
  { value: 'french-manicure', label: 'French Manicure' },
  { value: 'gel-nails', label: 'Gel Nails' },
  { value: 'acrylic-nails', label: 'Acrylic Nails' },
  { value: 'nail-art', label: 'Nail Art' },
  { value: 'rhinestones', label: 'Rhinestones' },
  { value: 'charms', label: 'Charms' },
  { value: 'studs', label: 'Studs' },
  { value: 'gradient-ombres', label: 'Gradient/Ombré' },
  { value: 'marble-nails', label: 'Marble Nails' },
  { value: 'chrome-nails', label: 'Chrome Nails' },
  { value: 'floral-nails', label: 'Floral Nails' },
  { value: 'animal-print', label: 'Animal Print' },
  { value: 'spa-pedicure', label: 'Spa Pedicure' },
  { value: 'classic-pedicure', label: 'Classic Pedicure' },
  { value: 'gel-pedicure', label: 'Gel Pedicure' },
  { value: 'tattoos', label: 'Tattoos' },
  { value: 'piercings', label: 'Piercings' },
  { value: 'foot-jewelry', label: 'Foot Jewelry' },
  { value: 'ankle-bracelets', label: 'Ankle Bracelets' },
  { value: 'toe-rings', label: 'Toe Rings' },
  { value: 'soft-soles', label: 'Soft Soles' },
  { value: 'rough-soles', label: 'Rough Soles' },
  { value: 'smooth-soles', label: 'Smooth Soles' },
  { value: 'ticklish-feet', label: 'Ticklish Feet' },
  { value: 'arched-feet', label: 'Arched Feet' },
  { value: 'flat-feet', label: 'Flat Feet' },
  { value: 'high-arches', label: 'High Arches' },
  { value: 'moisturized-feet', label: 'Moisturized Feet' },
  { value: 'fresh-feet', label: 'Fresh Feet' },
  { value: 'barefoot', label: 'Barefoot' },
  { value: 'socks', label: 'Socks' },
  { value: 'stockings', label: 'Stockings' },
  { value: 'heels', label: 'Heels' },
  { value: 'high-heels', label: 'High Heels' },
  { value: 'sandals', label: 'Sandals' },
  { value: 'boots', label: 'Boots' },
  { value: 'custom-requests', label: 'Custom Requests' },
];

const messageTypeOptions = [
  'Let me spoil you 💅',
  'Do what I tell you to do 👑',
  'Tell me what you want 💬',
  'Spoil me and I\'ll surprise you ✨',
  'Custom requests welcome 🎨',
  'Worship my feet 🙏',
  'Rate my pedicure 💜',
];

const yesNoOptions = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'sometimes', label: 'Sometimes' },
];

const STEPS = [
  { label: 'Profile Picture', icon: <IconCamera size="1rem" /> },
  { label: 'General Info',    icon: <IconUser size="1rem" /> },
  { label: 'Physical Info',   icon: <IconInfoCircle size="1rem" /> },
  { label: 'Preferences',     icon: <IconHeart size="1rem" /> },
  { label: 'Gallery',         icon: <IconPhoto size="1rem" /> },
  { label: 'Tags',            icon: <IconSettings size="1rem" /> },
  { label: 'Bank Info',       icon: <IconBuildingBank size="1rem" /> },
];

// ── Step components ──────────────────────────────────────────────────────────

const StepProfilePicture = ({ profilePic, onUpload }) => (
  <Stack gap="lg" align="center">
    <Title order={3}>Upload Your Profile Picture</Title>
    <Text size="sm" c="dimmed" ta="center">
      This is the first thing buyers will see. Make it count! <strong>Required.</strong>
    </Text>
    <Box style={{ position: 'relative' }}>
      {profilePic ? (
        <Image src={profilePic} alt="Profile" w={160} h={160} radius="50%"
          style={{ border: '4px solid #9f38cc' }} />
      ) : (
        <Box w={160} h={160} style={{
          borderRadius: '50%', border: '3px dashed #ced4da',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: '#f8f9fa'
        }}>
          <IconCamera size={52} color="#adb5bd" />
        </Box>
      )}
    </Box>
    <FileButton onChange={onUpload} accept="image/*">
      {(props) => (
        <Button {...props} color="grape" variant={profilePic ? 'light' : 'filled'} size="md">
          {profilePic ? 'Change Photo' : 'Upload Photo'}
        </Button>
      )}
    </FileButton>
    {!profilePic && <Text size="sm" c="red">A profile picture is required to continue.</Text>}
  </Stack>
);

const StepGeneralInfo = ({ data, onChange }) => (
  <Stack gap="md">
    <Title order={3}>General Info</Title>
    <Text size="sm" c="dimmed">Tell buyers a bit about yourself.</Text>
    <TextInput
      label="Display Name"
      placeholder="The name buyers will see"
      value={data.displayName}
      onChange={(e) => onChange('displayName', e.currentTarget.value)}
      maxLength={100}
      required
    />
    <Textarea
      label="Bio"
      placeholder="Tell buyers about yourself, your style, what makes you unique..."
      minRows={4}
      maxRows={6}
      maxLength={300}
      value={data.bio}
      onChange={(e) => onChange('bio', e.currentTarget.value)}
      autosize
    />
    <Text size="xs" c="dimmed" ta="right">{data.bio.length}/300</Text>
  </Stack>
);

const StepPhysicalInfo = ({ data, onChange }) => {
  const raceOptions = [
    { value: 'asian', label: 'Asian' },
    { value: 'black', label: 'Black or African American' },
    { value: 'hispanic', label: 'Hispanic or Latino' },
    { value: 'native', label: 'Native American' },
    { value: 'pacific', label: 'Pacific Islander' },
    { value: 'white', label: 'White' },
    { value: 'mixed', label: 'Mixed Race' },
    { value: 'other', label: 'Other' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' },
  ];
  return (
    <Stack gap="md">
      <Title order={3}>Physical Info</Title>
      <Text size="sm" c="dimmed">All optional — share what you're comfortable with.</Text>
      <Group grow>
        <NumberInput label="Age" placeholder="Your age" min={18} max={99}
          value={data.age} onChange={(v) => onChange('age', v)} />
        <NumberInput label="Weight (lbs)" placeholder="lbs" min={50} max={600}
          value={data.weight} onChange={(v) => onChange('weight', v)} />
      </Group>
      <Group grow>
        <TextInput label="Height" placeholder="e.g. 5'6&quot;" value={data.height}
          onChange={(e) => onChange('height', e.currentTarget.value)} />
        <NumberInput label="Number of Toes" min={0} max={12} value={data.numberOfToes}
          onChange={(v) => onChange('numberOfToes', v)} />
      </Group>
      <Select label="Race / Ethnicity" placeholder="Select..." data={raceOptions}
        value={data.race} onChange={(v) => onChange('race', v)} clearable />
    </Stack>
  );
};

const StepPreferences = ({ data, onChange }) => (
  <Stack gap="xl">
    <Title order={3}>Preferences</Title>

    {/* Message types */}
    <Stack gap="sm">
      <Text fw={600} size="sm">Types of messages I like:</Text>
      <Text size="xs" c="dimmed">Select all that apply</Text>
      <Group gap="xs" wrap="wrap">
        {messageTypeOptions.map((msg) => (
          <Chip
            key={msg}
            checked={data.messageTypes.includes(msg)}
            onChange={() => {
              const next = data.messageTypes.includes(msg)
                ? data.messageTypes.filter(m => m !== msg)
                : [...data.messageTypes, msg];
              onChange('messageTypes', next);
            }}
            color="grape"
            size="sm"
          >
            {msg}
          </Chip>
        ))}
      </Group>
    </Stack>

    <Divider />

    {/* Categories */}
    <Stack gap="sm">
      <Text fw={600} size="sm">Categories:</Text>
      <Text size="xs" c="dimmed">Pick tags that describe your style (up to 10)</Text>
      <Chip.Group
        multiple
        value={data.categories}
        onChange={(vals) => {
          if (vals.length <= 10) onChange('categories', vals);
        }}
      >
        <Group gap="xs" wrap="wrap">
          {footAttributes.map((attr) => (
            <Chip
              key={attr.value}
              value={attr.value}
              color="pink"
              size="sm"
              disabled={data.categories.length >= 10 && !data.categories.includes(attr.value)}
            >
              {attr.label}
            </Chip>
          ))}
        </Group>
      </Chip.Group>
      <Text size="xs" c="dimmed">{data.categories.length}/10 selected</Text>
    </Stack>

    <Divider />

    {/* Meet / Face / Active */}
    <Group grow wrap="wrap">
      <Select
        label="Willing to meet in person"
        data={yesNoOptions}
        value={data.willingToMeet}
        onChange={(v) => onChange('willingToMeet', v)}
        placeholder="Select..."
      />
      <Select
        label="Willing to show face"
        data={yesNoOptions}
        value={data.willingToShowFace}
        onChange={(v) => onChange('willingToShowFace', v)}
        placeholder="Select..."
      />
    </Group>

    <Textarea
      label="Times I'm active"
      placeholder="e.g. Evenings and weekends EST, usually respond within a few hours..."
      minRows={2}
      maxRows={4}
      value={data.activeTimes}
      onChange={(e) => onChange('activeTimes', e.currentTarget.value)}
      autosize
    />
  </Stack>
);

// Duration check helper
function getVideoDuration(file) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => { URL.revokeObjectURL(video.src); resolve(video.duration); };
    video.onerror = () => reject(new Error('Could not read video'));
    video.src = URL.createObjectURL(file);
  });
}

function GalleryPanel({ isPublic, items, onAdd, onRemove, error, onClearError }) {
  const inputRef = useRef(null);

  const photos = items.filter(i => i.type === 'image');
  const videos = items.filter(i => i.type === 'video');
  const atPhotoLimit = isPublic && photos.length >= 15;
  const atVideoLimit = isPublic && videos.length >= 3;
  const isFull = atPhotoLimit && atVideoLimit;

  return (
    <Stack gap="md">
      {/* Banner */}
      <Alert
        color={isPublic ? 'blue' : 'grape'}
        variant="light"
        icon={isPublic ? <IconEye size="1rem" /> : <IconLock size="1rem" />}
      >
        {isPublic ? (
          <Text size="xs">
            ⚠️ All content in your public gallery is <strong>visible to all users</strong>, including
            visitors who haven't signed in. Upload up to <strong>15 photos</strong> and{' '}
            <strong>3 videos (max 10 seconds each)</strong>. Not required but recommended.
          </Text>
        ) : (
          <Text size="xs">
            🔒 Your private gallery is <strong>blurred for all visitors</strong> until a buyer spoils
            you with a pedicure — then it unlocks just for them. Post{' '}
            <strong>unlimited photos</strong> and <strong>unlimited videos up to 5 minutes</strong>.
          </Text>
        )}
      </Alert>

      {/* Limits row (public only) */}
      {isPublic && (
        <Group gap="xl">
          <Text size="xs" c={atPhotoLimit ? 'red' : 'dimmed'}>
            Photos: <strong>{photos.length}/15</strong>
          </Text>
          <Text size="xs" c={atVideoLimit ? 'red' : 'dimmed'}>
            Videos (10 sec max): <strong>{videos.length}/3</strong>
          </Text>
        </Group>
      )}
      {!isPublic && items.length > 0 && (
        <Text size="xs" c="dimmed">{items.length} item{items.length !== 1 ? 's' : ''} staged</Text>
      )}

      {/* Error */}
      {error && (
        <Alert color="red" withCloseButton onClose={onClearError} py="xs">
          <Text size="xs" style={{ whiteSpace: 'pre-line' }}>{error}</Text>
        </Alert>
      )}

      {/* Preview grid */}
      <SimpleGrid cols={{ base: 3, sm: 4, md: 5 }} spacing="sm">
        {items.map((item, index) => (
          <Box key={item.id} style={{ position: 'relative', height: 90, borderRadius: 8, overflow: 'hidden', background: '#111' }}>
            {item.type === 'video'
              ? <video src={item.preview} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <img src={item.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
            {item.type === 'video' && (
              <Badge size="xs" color="blue" variant="filled" style={{ position: 'absolute', bottom: 4, left: 4, fontSize: 9 }}>
                <IconVideo size="0.6rem" />
              </Badge>
            )}
            <Button size="xs" color="red" variant="filled"
              style={{ position: 'absolute', top: 3, right: 3, padding: 0, minWidth: 20, width: 20, height: 20 }}
              onClick={() => onRemove(index)}>
              <IconX size={11} />
            </Button>
          </Box>
        ))}
        {!isFull && (
          <Box
            onClick={() => inputRef.current?.click()}
            style={{
              height: 90, border: '2px dashed #ced4da', borderRadius: 8,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', cursor: 'pointer', backgroundColor: '#f8f9fa', gap: 4
            }}
          >
            <IconPlus size={24} color="#adb5bd" />
            <Text size={10} c="dimmed">{isPublic ? 'Add' : 'Add private'}</Text>
          </Box>
        )}
      </SimpleGrid>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        style={{ display: 'none' }}
        onChange={(e) => { if (e.target.files) { onAdd(e.target.files); e.target.value = ''; } }}
      />
    </Stack>
  );
}

const StepGallery = ({ publicItems, privateItems, onPublicAdd, onPrivateAdd, onPublicRemove, onPrivateRemove, publicError, privateError, onClearPublicError, onClearPrivateError }) => (
  <Stack gap="md">
    <Title order={3}>Gallery</Title>
    <Tabs defaultValue="public" color="grape">
      <Tabs.List>
        <Tabs.Tab value="public" leftSection={<IconEye size="0.85rem" />}>
          Public Gallery
          {publicItems.length > 0 && <Badge size="xs" color="blue" variant="light" ml={6}>{publicItems.length}</Badge>}
        </Tabs.Tab>
        <Tabs.Tab value="private" leftSection={<IconLock size="0.85rem" />}>
          Private Gallery
          {privateItems.length > 0 && <Badge size="xs" color="grape" variant="light" ml={6}>{privateItems.length}</Badge>}
        </Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="public" pt="md">
        <GalleryPanel
          isPublic={true}
          items={publicItems}
          onAdd={onPublicAdd}
          onRemove={onPublicRemove}
          error={publicError}
          onClearError={onClearPublicError}
        />
      </Tabs.Panel>
      <Tabs.Panel value="private" pt="md">
        <GalleryPanel
          isPublic={false}
          items={privateItems}
          onAdd={onPrivateAdd}
          onRemove={onPrivateRemove}
          error={privateError}
          onClearError={onClearPrivateError}
        />
      </Tabs.Panel>
    </Tabs>
  </Stack>
);

const StepTags = ({ tags, onChange }) => (
  <Stack gap="md">
    <Title order={3}>Your Foot Tags</Title>
    <Text size="sm" c="dimmed">
      Select tags that describe your foot attributes — these show up on your public profile and
      help buyers find you.
    </Text>
    <Chip.Group multiple value={tags} onChange={onChange}>
      <Group gap="xs" wrap="wrap">
        {footAttributes.map((attr) => (
          <Chip key={attr.value} value={attr.value} color="grape" size="sm">
            {attr.label}
          </Chip>
        ))}
      </Group>
    </Chip.Group>
    {tags.length > 0 && (
      <Group gap="xs" wrap="wrap">
        <Text size="sm" c="dimmed" fw={500}>Selected ({tags.length}):</Text>
        {tags.map(t => {
          const attr = footAttributes.find(a => a.value === t);
          return attr ? <Badge key={t} size="sm" color="grape" variant="light">{attr.label}</Badge> : null;
        })}
      </Group>
    )}
  </Stack>
);

const StepBankInfo = ({ data, onChange }) => (
  <Stack gap="md">
    <Title order={3}>Bank Information</Title>
    <Text size="sm" c="dimmed">
      Enter your banking details to receive payouts when buyers spoil you. Your information is
      encrypted and stored securely.
    </Text>

    <Alert color="blue" variant="light" icon={<IconShieldCheck size="1rem" />}>
      <Text size="xs">
        Your banking details are used solely for sending your earnings. We use bank-level encryption
        to keep your information safe.
      </Text>
    </Alert>

    <TextInput
      label="Account Holder Name"
      placeholder="Full name as it appears on your bank account"
      value={data.accountHolderName}
      onChange={(e) => onChange('accountHolderName', e.currentTarget.value)}
    />

    <TextInput
      label="Bank Name"
      placeholder="e.g. Chase, Bank of America, Wells Fargo..."
      value={data.bankName}
      onChange={(e) => onChange('bankName', e.currentTarget.value)}
    />

    <Group grow>
      <TextInput
        label="Routing Number"
        placeholder="9-digit routing number"
        maxLength={9}
        value={data.routingNumber}
        onChange={(e) => onChange('routingNumber', e.currentTarget.value.replace(/\D/g, ''))}
      />
      <PasswordInput
        label="Account Number"
        placeholder="Your account number"
        value={data.accountNumber}
        onChange={(e) => onChange('accountNumber', e.currentTarget.value.replace(/\D/g, ''))}
      />
    </Group>

    <Paper p="md" withBorder radius="md" style={{ background: '#fafafa' }}>
      <Stack gap="xs">
        <Text size="xs" fw={600} c="dimmed">PAYOUT SCHEDULE</Text>
        <Text size="sm">Earnings are paid out weekly every Friday. Minimum payout is $10.</Text>
      </Stack>
    </Paper>
  </Stack>
);

// ── Main component ───────────────────────────────────────────────────────────

const BuildProfilePage = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [completed, setCompleted] = useState(false);

  // Step 1 — Profile picture
  const [profilePic, setProfilePic] = useState(null);

  // Step 2 — General info
  const [generalInfo, setGeneralInfo] = useState({ displayName: '', bio: '' });

  // Step 3 — Physical info
  const [physicalInfo, setPhysicalInfo] = useState({
    age: '', weight: '', height: '', numberOfToes: 10, race: ''
  });

  // Step 4 — Preferences
  const [preferences, setPreferences] = useState({
    messageTypes: [],
    categories: [],
    willingToMeet: null,
    willingToShowFace: null,
    activeTimes: '',
  });

  // Step 5 — Gallery
  const [publicGallery, setPublicGallery] = useState([]);
  const [privateGallery, setPrivateGallery] = useState([]);
  const [publicGalleryError, setPublicGalleryError] = useState(null);
  const [privateGalleryError, setPrivateGalleryError] = useState(null);

  // Step 6 — Tags
  const [tags, setTags] = useState([]);

  // Step 7 — Bank info
  const [bankInfo, setBankInfo] = useState({
    accountHolderName: '',
    bankName: '',
    routingNumber: '',
    accountNumber: '',
  });

  const handleGeneralChange = (field, value) =>
    setGeneralInfo(prev => ({ ...prev, [field]: value }));

  const handlePhysicalChange = (field, value) =>
    setPhysicalInfo(prev => ({ ...prev, [field]: value }));

  const handlePrefChange = (field, value) =>
    setPreferences(prev => ({ ...prev, [field]: value }));

  const handleBankInfoChange = (field, value) =>
    setBankInfo(prev => ({ ...prev, [field]: value }));

  const buildGalleryItems = async (files, isPublic) => {
    const errors = [];
    const items = [];
    const MAX_VIDEO_SEC = isPublic ? 10 : 300;

    for (const file of Array.from(files)) {
      const isImg = file.type.startsWith('image/');
      const isVid = file.type.startsWith('video/');
      if (!isImg && !isVid) { errors.push(`${file.name}: only images and videos`); continue; }

      if (isPublic) {
        const curPhotos = publicGallery.filter(i => i.type === 'image').length + items.filter(i => i.type === 'image').length;
        const curVideos = publicGallery.filter(i => i.type === 'video').length + items.filter(i => i.type === 'video').length;
        if (isImg && curPhotos >= 15) { errors.push(`${file.name}: public gallery is full (15 photos max)`); continue; }
        if (isVid && curVideos >= 3)  { errors.push(`${file.name}: public gallery is full (3 videos max)`); continue; }
      }

      if (isVid) {
        try {
          const dur = await getVideoDuration(file);
          if (dur > MAX_VIDEO_SEC) {
            const label = isPublic ? '10 seconds' : '5 minutes';
            errors.push(`${file.name}: video too long (max ${label})`);
            continue;
          }
        } catch { errors.push(`${file.name}: could not verify duration`); continue; }
      }

      items.push({ id: `${Date.now()}-${Math.random()}`, preview: URL.createObjectURL(file), file, type: isImg ? 'image' : 'video' });
    }

    return { items, errors };
  };

  const handlePublicGalleryAdd = async (files) => {
    const { items, errors } = await buildGalleryItems(files, true);
    if (errors.length) setPublicGalleryError(errors.join('\n'));
    if (items.length) setPublicGallery(prev => [...prev, ...items]);
  };

  const handlePrivateGalleryAdd = async (files) => {
    const { items, errors } = await buildGalleryItems(files, false);
    if (errors.length) setPrivateGalleryError(errors.join('\n'));
    if (items.length) setPrivateGallery(prev => [...prev, ...items]);
  };

  const canAdvance = () => {
    if (active === 0) return !!profilePic;
    if (active === 1) return !!generalInfo.displayName.trim();
    return true;
  };

  const handleNext = () => {
    if (active < STEPS.length - 1) {
      setActive(a => a + 1);
    } else {
      setCompleted(true);
      setTimeout(() => navigate('/seller/dashboard'), 1800);
    }
  };

  const handleBack = () => setActive(a => Math.max(0, a - 1));

  // Jump back to a completed step when clicking its icon
  const handleStepClick = (index) => {
    if (index < active) setActive(index);
  };

  if (completed) {
    return (
      <Container size="sm" py="xl">
        <Card shadow="md" padding="xl" radius="md" withBorder>
          <Stack align="center" gap="md">
            <ThemeIcon size={72} radius="50%" color="pink" variant="gradient"
              gradient={{ from: 'pink', to: 'grape', deg: 45 }}>
              <IconCheck size="2rem" />
            </ThemeIcon>
            <Title order={2} ta="center" c="grape">You're all set, Princess! 💅</Title>
            <Text ta="center" c="dimmed">Taking you to your dashboard…</Text>
          </Stack>
        </Card>
      </Container>
    );
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="xl">
        <Center>
          <Stack align="center" gap="xs" maw={500}>
            <Title order={2} ta="center" c="grape">Build Your Princess Profile</Title>
            <Text size="sm" c="dimmed" ta="center">
              Complete each step to set up your profile and start earning.
            </Text>
          </Stack>
        </Center>

        {/* Progress Stepper — completed steps stay pink + keep original icon; click to go back */}
        <Stepper
          active={active}
          color="pink"
          size="sm"
          styles={{
            stepLabel: { fontSize: 11 },
            stepBody: { display: 'none' },
          }}
        >
          {STEPS.map((step, index) => (
            <Stepper.Step
              key={step.label}
              label={step.label}
              icon={step.icon}
              completedIcon={step.icon}
              onClick={() => handleStepClick(index)}
              style={{ cursor: index < active ? 'pointer' : 'default' }}
            />
          ))}
        </Stepper>

        {active > 0 && (
          <Text size="xs" c="dimmed" ta="center">
            💡 Click any pink completed step to go back and make edits
          </Text>
        )}

        {/* Step Content */}
        <Card shadow="sm" padding="xl" radius="lg" withBorder>
          <Stack gap="lg">
            <Group gap="xs">
              <Badge color="pink" variant="filled" size="lg" circle>
                {active + 1}
              </Badge>
              <Text fw={700} size="lg" c="grape">{STEPS[active].label}</Text>
            </Group>
            <Divider />

            {active === 0 && (
              <StepProfilePicture
                profilePic={profilePic}
                onUpload={(file) => file && setProfilePic(URL.createObjectURL(file))}
              />
            )}
            {active === 1 && (
              <StepGeneralInfo data={generalInfo} onChange={handleGeneralChange} />
            )}
            {active === 2 && (
              <StepPhysicalInfo data={physicalInfo} onChange={handlePhysicalChange} />
            )}
            {active === 3 && (
              <StepPreferences data={preferences} onChange={handlePrefChange} />
            )}
            {active === 4 && (
              <StepGallery
                publicItems={publicGallery}
                privateItems={privateGallery}
                onPublicAdd={handlePublicGalleryAdd}
                onPrivateAdd={handlePrivateGalleryAdd}
                onPublicRemove={(i) => setPublicGallery(prev => prev.filter((_, idx) => idx !== i))}
                onPrivateRemove={(i) => setPrivateGallery(prev => prev.filter((_, idx) => idx !== i))}
                publicError={publicGalleryError}
                privateError={privateGalleryError}
                onClearPublicError={() => setPublicGalleryError(null)}
                onClearPrivateError={() => setPrivateGalleryError(null)}
              />
            )}
            {active === 5 && (
              <StepTags tags={tags} onChange={setTags} />
            )}
            {active === 6 && (
              <StepBankInfo data={bankInfo} onChange={handleBankInfoChange} />
            )}
          </Stack>
        </Card>

        {/* Navigation */}
        <Group justify="space-between">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size="1rem" />}
            onClick={handleBack}
            disabled={active === 0}
          >
            Back
          </Button>
          <Text size="xs" c="dimmed">Step {active + 1} of {STEPS.length}</Text>
          <Button
            color={active === STEPS.length - 1 ? 'pink' : 'grape'}
            variant={active === STEPS.length - 1 ? 'gradient' : 'filled'}
            gradient={active === STEPS.length - 1 ? { from: 'pink', to: 'grape', deg: 45 } : undefined}
            rightSection={active < STEPS.length - 1 ? <IconArrowRight size="1rem" /> : <IconCheck size="1rem" />}
            onClick={handleNext}
            disabled={!canAdvance()}
            size={active === STEPS.length - 1 ? 'md' : 'sm'}
          >
            {active < STEPS.length - 1 ? 'Continue' : "Let's Go 💅"}
          </Button>
        </Group>
      </Stack>
    </Container>
  );
};

export default BuildProfilePage;
